import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';

import Loader from '../../components/Loader/Loader';
import { AuthState } from '@clatter/platform';

LicenseInfo.setLicenseKey(process.env.NX_PUBLIC_MUI_LICENSE_KEY);

const RootProvider = ({ store, App, usePersistence = true }) => {
  const persistor = usePersistence ? persistStore(store) : null;

  const content = (
    <StrictMode>
      <BrowserRouter>
        <AuthState>
          <Sentry.ErrorBoundary
            fallback={({ error }) => (
              <div style={{ fontFamily: 'Arial, sans-serif', textAlign: 'center', padding: '50px', backgroundColor: '#f9f9f9', color: '#333', minHeight: '100vh' }}>
                <div style={{ display: 'inline-block', background: '#fff', padding: '40px 60px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  <img src="https://getclatter.com/wp-content/uploads/2023/01/clatter_logo.png" alt="Clatter Logo" style={{ maxWidth: '200px', marginBottom: '30px' }} />
                  <h1 style={{ fontSize: '28px', marginBottom: '20px' }}>Something went wrong</h1>
                  <p style={{ fontSize: '16px', lineHeight: '1.5' }}>An unexpected error occurred. Please refresh the page or contact support if the problem persists.</p>
                  {process.env.NODE_ENV !== 'production' && <p style={{ fontSize: '14px', color: '#999', marginTop: '16px' }}>{error?.message}</p>}
                </div>
              </div>
            )}
          >
            <App />
          </Sentry.ErrorBoundary>
        </AuthState>
      </BrowserRouter>
    </StrictMode>
  );

  // 'home' app is not using persist gate...
  return (
    <Provider store={store}>
      {usePersistence ? (
        <PersistGate loading={<Loader />} persistor={persistor}>
          {content}
        </PersistGate>
      ) : (
        content
      )}
    </Provider>
  );
};

export const renderApp = (store, App, rootElementId = 'root', usePersistence = true) => {
  const root = createRoot(document.getElementById(rootElementId));
  root.render(<RootProvider store={store} App={App} usePersistence={usePersistence} />);
};
